/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback } from "react";
import { atom, useRecoilState } from "recoil";

import api, { riderId } from "../Api";

import BaseAddress from "../../models/BaseAddress";

const baseAddressesAtom = atom({
  key: "baseAddresses",
  default: null,
});

const baseAddressesByBuildingDongNumberAtom = atom({
  key: "baseAddressesByBuildingDongNumber",
  default: null,
});

// 배송상세 하이라이트 섹션의 주소정보 표시가 참조하는 bookId.
// fetchAllById가 완료된 배송건의 bookId를 저장해두고, 화면에서 현재
// 조회 중인 delivery.bookId와 비교해 다른 배송건의 잔여 상태(이전
// 배송건 조회 결과)가 잠깐 잘못 노출되는 것을 방지한다.
// (원본 RN 앱의 baseAddressesState?.bookId === id 가드와 동일한 목적)
const baseAddressesBookIdAtom = atom({
  key: "baseAddressesBookId",
  default: null,
});

const baseAddressesObjAtom = atom({
  key: "baseAddressesObj",
  default: {
    currentBuildingDong: [],
    buildingDongWithAddressRoad: [],
    buildingDongWithAddress: [],
    noBuildingDongWithAddressRoad: [],
    noBuildingDongWithAddress: [],
  },
});

const baseAddressesAllAtom = atom({
  key: "baseAddressesAll",
  default: null,
});

const baseAddressIdAtom = atom({
  key: "baseAddressId",
  default: null,
});

const baseAddressesFetchedAtom = atom({
  key: "baseAddressesFetched",
  default: false,
});

const frontdoorPasswordsByAddressAtom = atom({
  key: "frontdoorPasswordsByAddress",
  default: [],
});

const isCollectionRestrictedAtom = atom({
  key: "isCollectionRestricted",
  default: false,
});

export const useBaseAddressesStore = () => {
  const [baseAddresses, setBaseAddresses] = useRecoilState(baseAddressesAtom);
  const [
    baseAddressesByBuildingDongNumber,
    setBaseAddressesByBuildingDongNumber,
  ] = useRecoilState(baseAddressesByBuildingDongNumberAtom);
  const [baseAddressesBookId, setBaseAddressesBookId] = useRecoilState(
    baseAddressesBookIdAtom,
  );
  const [baseAddressesAll, setBaseAddressesAll] =
    useRecoilState(baseAddressesAllAtom);
  const [baseAddressesObj, setBaseAddressesObj] =
    useRecoilState(baseAddressesObjAtom);
  const [baseAddressId, setBaseAddressId] = useRecoilState(baseAddressIdAtom);
  const [baseAddressesFetched, setBaseAddressesFetched] = useRecoilState(
    baseAddressesFetchedAtom,
  );
  const [frontdoorPasswordsByAddress, setFrontdoorPasswordsByAddress] =
    useRecoilState(frontdoorPasswordsByAddressAtom);
  const [isCollectionRestricted, setIsCollectionRestricted] = useRecoilState(
    isCollectionRestrictedAtom,
  );

  const create = useCallback(
    async (data) => await api.post(`/base-addresses/memos`, data),
    [api.post],
  );

  const deleteBaseAddress = useCallback(
    async (id) => {
      return await api.delete(`/base-addresses/memos/${id}`);
    },
    [api.delete],
  );

  const deactivateFrontdoorPassword = useCallback(
    async (id) => {
      return await api.put(
        `/base-address-frontdoor-passwords/${id}/deactivate`,
      );
    },
    [api.put],
  );

  const fetchAllById = useCallback(
    async ({ bookId, buildingDongNumber, address, addressRoad }) => {
      if (bookId) {
        const {
          noBuildingDong: _noBuildingDong,
          withBuildingDong: _withBuildingDong,
        } = await api.get(`/deliveries/${bookId}/base-addresses/memos/all`);

        const noBuildingDong = _noBuildingDong.sort((a, b) => {
          return a.order < b.order ? -1 : 1;
        });

        const withBuildingDong = _withBuildingDong.sort((a, b) => {
          return a.order < b.order ? -1 : 1;
        });

        let _currentBuildingDong = []; // 도로명 주소 일치 / 빌딩동 정보 일치
        let _withBuildingDongWithAddressRoad = []; // 도로명 주소 일치 / 빌딩동 정보 불일치
        let _withBuildingDongWithAddress = []; // 도로명 주소 불일치
        let _noBuildingDongWithAddressRoad = []; // 도로명 주소 일치
        let _noBuildingDongWithAddress = []; // 도로명 주소 불일치

        if (withBuildingDong?.length > 0) {
          withBuildingDong.forEach((el) => {
            if (
              `${el.buildingDongNumber}동` === buildingDongNumber &&
              addressRoad === el.baseAddress?.addressRoad
            ) {
              _currentBuildingDong.push(el);
            } else if (addressRoad === el.baseAddress?.addressRoad) {
              _withBuildingDongWithAddressRoad.push(el);
            } else if (
              address === el.baseAddress?.address ||
              address + "번지" === el.baseAddress?.address ||
              address === el.baseAddress?.address + "번지"
            ) {
              _withBuildingDongWithAddress.push(el);
            }
          });
        }

        if (noBuildingDong?.length > 0) {
          noBuildingDong.forEach((el) => {
            if (addressRoad === el.baseAddress?.addressRoad) {
              _noBuildingDongWithAddressRoad.push(el);
            } else if (
              address === el.baseAddress?.address ||
              address + "번지" === el.baseAddress?.address ||
              address === el.baseAddress?.address + "번지"
            ) {
              _noBuildingDongWithAddress.push(el);
            }
          });
        }

        let baseAddresses = [
          ..._currentBuildingDong,
          ..._withBuildingDongWithAddressRoad,
          ..._noBuildingDongWithAddressRoad,
          ..._withBuildingDongWithAddress,
          ..._noBuildingDongWithAddress,
          ...noBuildingDong,
        ];

        // 배송상세 하이라이트 섹션의 주소정보 표시용.
        // 원본 RN 앱의 baseAddresses[0] 우선순위(도로명+빌딩동 일치 >
        // 빌딩동만 불일치 도로명일치 > 도로명만 일치(빌딩동 없음) >
        // 지번주소 일치(빌딩동 불일치) > 지번주소 일치(빌딩동 없음) >
        // 그 외 전체)와 동일한 순서로 맞춘다.
        let baseAddressesByBuildingDongNumber = [
          ..._currentBuildingDong,
          ..._withBuildingDongWithAddressRoad,
          ..._noBuildingDongWithAddressRoad,
          ..._withBuildingDongWithAddress,
          ..._noBuildingDongWithAddress,
          ...noBuildingDong,
        ];

        setBaseAddresses(baseAddresses.map((b) => new BaseAddress(b)));
        setBaseAddressesByBuildingDongNumber(
          baseAddressesByBuildingDongNumber.map((b) => new BaseAddress(b)),
        );
        setBaseAddressesBookId(bookId);
        setBaseAddressesAll([...noBuildingDong, ...withBuildingDong]);
        setBaseAddressesObj({
          currentBuildingDong: _currentBuildingDong.map(
            (b) => new BaseAddress(b),
          ),
          buildingDongWithAddressRoad: _withBuildingDongWithAddressRoad.map(
            (b) => new BaseAddress(b),
          ),
          buildingDongWithAddress: _withBuildingDongWithAddress.map(
            (b) => new BaseAddress(b),
          ),
          noBuildingDongWithAddressRoad: _noBuildingDongWithAddressRoad.map(
            (b) => new BaseAddress(b),
          ),
          noBuildingDongWithAddress: _noBuildingDongWithAddress.map(
            (b) => new BaseAddress(b),
          ),
        });
        setBaseAddressesFetched(true);
      }
    },
    [api.get, riderId],
  );

  const fetchBaseAddressId = useCallback(
    async ({ addressRoad }) => {
      const { items } = await api.get(`/base-addresses`, {
        page: 1,
        pageSize: 1000,
        addressRoad,
      });

      const ordereds = items.sort((a, b) => {
        return a.id > b.id ? -1 : 1;
      });

      setBaseAddressId(ordereds[0]?.id);

      return ordereds[0]?.id;
    },
    [api.get, riderId],
  );

  const fetchFrontdoorPasswordsByAddress = useCallback(
    async (query) => {
      const { rows } = await api.get(
        "/base-address-frontdoor-passwords/all",
        query,
      );

      // 호수(buildingHouseNumber) 필터링: 응답에 호수 정보가 있는 항목은
      // 조회 조건의 호수 끝 2자리와 일치하는 것만 노출한다.
      // (원본 RN 앱과 동일한 필터 — 다른 호수의 현관비밀번호가 노출되는 것을 방지)
      const filteredRows = (rows || []).filter((row) => {
        if (!row.buildingHouseNumber) {
          return true;
        }

        return (
          !!query?.buildingHouseNumber &&
          row.buildingHouseNumber ===
            query.buildingHouseNumber.toString().slice(-2)
        );
      });

      setFrontdoorPasswordsByAddress(filteredRows);

      return filteredRows;
    },
    [api.get],
  );

  const fetchCollectionRestrictedByAddress = useCallback(
    async (query) => {
      const { rows } = await api.get(
        "/base-address-frontdoor-passwords/collection-restrictions",
        query,
      );

      setIsCollectionRestricted((rows || []).length > 0);

      return rows || [];
    },
    [api.get],
  );

  /**
   * 주소 정보 수정
   */
  const update = useCallback(
    async (id, { memo, baseAddressId, buildingDongNumber }) => {
      let data = { memo };

      if (baseAddressId) {
        data.baseAddressId = baseAddressId;
      }

      if (buildingDongNumber) {
        data.buildingDongNumber = buildingDongNumber;
      }

      return await api.put(`/base-addresses/memos/${id}`, data);
    },
    [api.put],
  );

  const updateOrder = useCallback(
    async (id, data) => {
      return await api.put(`/base-addresses/memos/${id}/order`, data);
    },
    [api.put],
  );

  const state = {
    baseAddresses,
    baseAddressesByBuildingDongNumber,
    baseAddressesBookId,
    baseAddressesAll,
    baseAddressesObj,
    baseAddressId,
    baseAddressesFetched,
    frontdoorPasswordsByAddress,
    isCollectionRestricted,
  };

  return {
    state,
    create,
    deleteBaseAddress,
    deactivateFrontdoorPassword,
    fetchAllById,
    fetchBaseAddressId,
    fetchFrontdoorPasswordsByAddress,
    fetchCollectionRestrictedByAddress,
    update,
    updateOrder,
  };
};
